import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import formDesignerService from '@api/v1/formDesignerService'
import { api } from '@api/v1/client'
import useDocumentTitle from '@hooks/useDocumentTitle'
import LoadingSpinner from '@components/common/LoadingSpinner'
import { toast } from '@store/toastStore'

const FormDesigner = () => {
  useDocumentTitle('Form Designer')
  const queryClient = useQueryClient()
  const [selectedType, setSelectedType] = useState('')

  const { data: typesData } = useQuery({
    queryKey: ['object-types-list'],
    queryFn: () => api.get('/objects/types'),
  })

  const { data: layoutsData, isLoading, error } = useQuery({
    queryKey: ['form-layouts', selectedType],
    queryFn: () => formDesignerService.getLayouts(selectedType),
    enabled: !!selectedType,
  })

  const deleteMutation = useMutation({
    mutationFn: ({ layoutId, objectType }) => formDesignerService.deleteLayout(layoutId, objectType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-layouts'] })
      toast.success('Layout deleted')
    },
    onError: (err) => toast.error(err.message || 'Failed to delete'),
  })

  const objectTypes = typesData?.data || []
  const layouts = Array.isArray(layoutsData?.data) ? layoutsData.data : []

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-display text-gray-900">Form Designer</h1>
        <p className="text-sm text-gray-500 mt-1">Manage form layouts for custom objects and fields</p>
      </div>

      {/* Object Type Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6 p-5">
        <label className="block text-sm font-medium text-gray-700 mb-2">Object Type</label>
        <select
          aria-label="Select object type"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="w-full max-w-md px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cb-accent/30"
        >
          <option value="">Select an object type...</option>
          {objectTypes.map((t) => (
            <option key={t.objectType || t.id} value={t.objectType || t.id}>
              {t.pluralName || t.singularName || t.name}
            </option>
          ))}
        </select>
      </div>

      {/* Layouts */}
      {selectedType && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Form Layouts ({layouts.length})</h2>
          </div>
          {isLoading ? (
            <div className="p-8 flex justify-center"><LoadingSpinner message="Loading layouts..." /></div>
          ) : error ? (
            <p className="p-6 text-sm text-red-500" role="alert">Failed to load layouts</p>
          ) : layouts.length === 0 ? (
            <p className="p-8 text-sm text-gray-500 text-center">No form layouts for this object type</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {layouts.map((layout, i) => {
                const id = layout.LayoutId || layout.Id || layout.id || i
                const name = layout.LayoutName || layout.Name || layout.name || `Layout ${i + 1}`
                const isDefault = layout.IsDefault || layout.isDefault
                return (
                  <div key={id} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50/50">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{name}</p>
                      {isDefault && (
                        <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded font-medium">Default</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {!isDefault && (
                        <button
                          onClick={() => formDesignerService.setDefaultLayout({
                            objectType: selectedType, layoutId: id,
                          }).then(() => {
                            queryClient.invalidateQueries({ queryKey: ['form-layouts'] })
                            toast.success('Default layout updated')
                          })}
                          className="text-xs text-cb-accent hover:text-cb-navy font-medium"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (confirm(`Delete layout "${name}"?`))
                            deleteMutation.mutate({ layoutId: id, objectType: selectedType })
                        }}
                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default FormDesigner
